import { JSX, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useGetIdentity, UseGetIdentityResult, useNotify } from 'react-admin';
import { useCreateEmailFinder, useGetFinderList } from './hooks/email-finder-container.hooks';
import { EmailFinder } from '../../../../../types/entities';
import { EmailFinderResultsBlock } from './EmailFinderResultsBlock';
import { EmailFinderCreateBar } from './components/EmailFinderCreateBar';
import { EmailFinderScanList } from './components/EmailFinderScanList';
import { isScanActive } from './email-finder-format';

const PAGE_SIZE: number = 10;
/** A running scan writes its counts as it goes, so the list refreshes itself. */
const POLL_MS: number = 8000;

export const EmailFinderContainer: () => JSX.Element = (): JSX.Element => {
    const user: UseGetIdentityResult = useGetIdentity();
    const notify: any = useNotify();
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedEmailFinder, setSelectedEmailFinder] = useState<EmailFinder>();
    const { creatingEmailFinder, createEmailFinder } = useCreateEmailFinder();
    const { emailFinders, loadingFinders, totalFinders, loadEmailFinders, finderError } = useGetFinderList();

    const adminId: string | number | undefined = user?.data?.id;
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The loader is rebuilt every render, so the poll reads it from here rather
    // than closing over the one that existed when the interval was set up.
    const load = useRef<(page: number, term: string) => void>((): void => undefined);

    load.current = (page: number, term: string): void => {
        loadEmailFinders(adminId, term, page);
    };

    function handleCreateEmailFinder(name: string, targetUrl: string, quickScan: boolean): void {
        if (adminId === undefined) {
            notify('Unable to assign the scan to the admin user', { type: 'error' });
            return;
        }
        createEmailFinder(name, targetUrl, adminId, quickScan)
            .then((): void => {
                setPageNumber(1);
                load.current(1, searchTerm);
            });
    }

    function handleSearch(term: string): void {
        setSearchTerm(term);
        setPageNumber(1);
        if (searchTimer.current !== null) {
            clearTimeout(searchTimer.current);
        }
        // Typing used to fire a request per keystroke, and each answer landed in
        // whatever order it came back in.
        searchTimer.current = setTimeout((): void => load.current(1, term), 400);
    }

    function handlePage(page: number): void {
        setPageNumber(page);
        load.current(page, searchTerm);
    }

    useEffect((): (() => void) => {
        if (adminId !== undefined) {
            load.current(pageNumber, searchTerm);
        }

        return (): void => {
            if (searchTimer.current !== null) {
                clearTimeout(searchTimer.current);
            }
        };
    }, [adminId]);

    // Keep the open scan's header and counts in step with the list underneath it.
    useEffect((): void => {
        if (selectedEmailFinder === undefined) {
            return;
        }
        const fresh: EmailFinder | undefined = emailFinders?.find(
            (item: EmailFinder): boolean => String(item.id) === String(selectedEmailFinder.id),
        );
        const moved: boolean = fresh !== undefined && (
            fresh.status !== selectedEmailFinder.status
            || fresh.totalFound !== selectedEmailFinder.totalFound
            || fresh.totalNew !== selectedEmailFinder.totalNew
        );
        if (moved) {
            setSelectedEmailFinder(fresh);
        }
    }, [emailFinders]);

    // A scan that is still working showed nothing until the tab was left and come
    // back to, which read as a scan that had hung.
    const anyRunning: boolean = (emailFinders ?? []).some(
        (item: EmailFinder): boolean => isScanActive(item.status),
    );

    useEffect((): (() => void) | undefined => {
        if (!anyRunning) {
            return undefined;
        }
        const poll: ReturnType<typeof setInterval> = setInterval((): void => {
            load.current(pageNumber, searchTerm);
        }, POLL_MS);

        return (): void => clearInterval(poll);
    }, [anyRunning, pageNumber, searchTerm]);

    return (
        <Box sx={ { width: '100%' } }>
            <EmailFinderCreateBar creating={ creatingEmailFinder }
                                  onCreate={ handleCreateEmailFinder } />

            <Box sx={ {
                display: 'flex',
                alignItems: 'stretch',
                gap: '20px',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
            } }>
                <Box sx={ {
                    flex: { xs: '1 1 100%', md: '0 0 300px' },
                    width: { xs: '100%', md: '300px' },
                    minWidth: 0,
                } }>
                    <EmailFinderScanList finders={ emailFinders }
                                         loading={ loadingFinders }
                                         error={ finderError }
                                         total={ totalFinders }
                                         page={ pageNumber }
                                         pageSize={ PAGE_SIZE }
                                         searchTerm={ searchTerm }
                                         selectedId={ selectedEmailFinder?.id }
                                         onSearch={ handleSearch }
                                         onPage={ handlePage }
                                         onSelect={ setSelectedEmailFinder }
                                         onRefresh={ (): void => load.current(pageNumber, searchTerm) } />
                </Box>

                <Box sx={ { flex: '1 1 auto', minWidth: 0 } }>
                    <EmailFinderResultsBlock emailFinder={ selectedEmailFinder } />
                </Box>
            </Box>
        </Box>
    );
}
